import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import {
  useAccounts,
  useCities,
  useIcdCodes,
  useInsurancePlanTypesByProvider,
  useInsuranceProviders,
  useSurgeryCenters,
  usePhysicians,
  useProcedures,
  useStates,
} from "../../hooks/useReferenceData";
import { useUpdateCaseRequisition } from "../../hooks/useWorkflows";
import PracticeForm, {
  PracticeFormValues,
} from "../admin/practices/PracticeForm";
import PhysicianForm, {
  PhysicianFormValues,
} from "../admin/physicians/PhysicianForm";
import SurgeryCenterLabForm, {
  SurgeryCenterLabFormValues,
} from "../admin/surgeryCentersLabs/SurgeryCenterLabForm";
import { useCreatePractice } from "../../hooks/admin/usePractices";
import { useCreatePhysician } from "../../hooks/admin/usePhysicians";
import { useCreateSurgeryCenterLab } from "../../hooks/admin/useSurgeryCentersLabs";
import { parseDateOnly, type DateOnlyString } from "../../utils/dateOnly";

export interface RequisitionCenterHandle {
  save: () => void;
  isSaving: boolean;
}

export interface LiveRequisitionData {
  patient: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string | null;
    sex?: string;
    medicalRecordNumber?: string;
  };
  physicianId?: string | null;
  physicianName?: string | null;
  dateReceived?: string | null;
  dateCollected?: string | null;
  sendingPracticeId?: string | null;
  surgeryCenterLabId?: string | null;
  surgeryCenterName?: string | null;
  clinicalHistoryNotes?: string;
  indications?: string;
  impressions?: string;
}

interface RequisitionCenterProps {
  caseData: any;
  isReadOnly?: boolean;
  onSave?: () => void;
  onAutoSaveStatusChange?: (
    status: "idle" | "saving" | "saved" | "error"
  ) => void;
  onFormValuesChange?: (data: LiveRequisitionData) => void;
}

interface FieldCount {
  filled: number;
  total: number;
}

// Helper: count filled fields in an object (handles nested)
function countFields(obj: Record<string, unknown>): FieldCount {
  return Object.values(obj).reduce<FieldCount>(
    (acc, v) => {
      if (
        v !== null &&
        v !== undefined &&
        typeof v === "object" &&
        !Array.isArray(v) &&
        !(v instanceof Date)
      ) {
        const nested = countFields(v as Record<string, unknown>);
        return {
          filled: acc.filled + nested.filled,
          total: acc.total + nested.total,
        };
      }
      const isFilled =
        v !== null &&
        v !== undefined &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0);
      return {
        filled: acc.filled + (isFilled ? 1 : 0),
        total: acc.total + 1,
      };
    },
    { filled: 0, total: 0 }
  );
}

// Sanitize dropdown options to prevent Mantine Select errors
function sanitizeOptions<T extends { value: string }>(
  options: T[] | undefined | null
): T[] {
  if (!Array.isArray(options)) return [];
  return options.filter(
    (opt): opt is T =>
      opt != null &&
      typeof opt === "object" &&
      "value" in opt &&
      opt.value != null &&
      opt.value !== ""
  );
}

function SimpleRichTextInput({
  label,
  placeholder,
  value,
  onChange,
  disabled,
}: {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: placeholder || "Enter text..." }),
      Underline,
      Link,
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingRef.current) return;
      const html = ed.getHTML();
      if (html !== lastValueRef.current) {
        lastValueRef.current = html;
        onChangeRef.current(html);
      }
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value !== lastValueRef.current) {
      isUpdatingRef.current = true;
      editor.commands.setContent(value || "");
      lastValueRef.current = value;
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [value, editor]);

  return (
    <Box>
      {label && (
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
      )}
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar sticky stickyOffset={0}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content />
      </RichTextEditor>
    </Box>
  );
}

const RequisitionCenter = forwardRef<
  RequisitionCenterHandle,
  RequisitionCenterProps
>(function RequisitionCenter(
  {
    caseData,
    isReadOnly = false,
    onSave,
    onAutoSaveStatusChange,
    onFormValuesChange,
  },
  ref
) {
  const [openedSections, setOpenedSections] = useState([
    "patient",
    "primaryInsurance",
    "caseInfo",
    "caseSettings",
  ]);

  const updateMutation = useUpdateCaseRequisition();

  // Quick-add modals
  const [
    practiceModalOpened,
    { open: openPracticeModal, close: closePracticeModal },
  ] = useDisclosure(false);
  const [
    surgeryCenterModalOpened,
    { open: openSurgeryCenterModal, close: closeSurgeryCenterModal },
  ] = useDisclosure(false);
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);

  // Quick-add mutations
  const createPractice = useCreatePractice();
  const createSurgeryCenter = useCreateSurgeryCenterLab();
  const createPhysician = useCreatePhysician();
  // Reference data
  const { data: states = [] } = useStates();
  const { data: practicesRaw = [] } = useAccounts();
  const { data: physiciansRaw = [] } = usePhysicians();
  const { data: proceduresRaw = [] } = useProcedures();
  const { data: icdCodesRaw = [] } = useIcdCodes();
  const { data: insuranceProvidersRaw = [] } = useInsuranceProviders();

  const form = useForm({
    initialValues: {
      patient: {
        firstName: "",
        lastName: "",
        dateOfBirth: null as DateOnlyString | null,
        sex: "" as "M" | "F" | "Other" | "",
        medicalRecordNumber: "",
        mobilePhone: "",
        homePhone: "",
        workPhone: "",
        email: "",
        address: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
        },
      },
      case: {
        sendingPracticeId: "" as string | null,
        surgeryCenterLabId: "" as string | null,
        physicianId: "" as string | null,
        procedureId: "" as string | null,
        externalCaseNumber: "",
        dateReceived: null as DateOnlyString | null,
        dateCollected: null as DateOnlyString | null,
        clinicalHistoryCodes: [] as string[],
        clinicalHistoryNotes: "",
        indications: "",
        impressions: "",
        primaryInsurance: {
          insuranceProvider: "",
          planType: "",
          policyNumber: "",
          authorizationNumber: "",
          groupNumber: "",
          groupName: "",
          patientRelationToInsured: "",
          address: { address: "", city: "", state: "", zipCode: "" },
        },
        secondaryInsurance: {
          insuranceProvider: "",
          planType: "",
          policyNumber: "",
          authorizationNumber: "",
          groupNumber: "",
          groupName: "",
          patientRelationToInsured: "",
          address: { address: "", city: "", state: "", zipCode: "" },
        },
        isRush: false,
        rushReason: "",
        isHighPriority: false,
        highPriorityReason: "",
      },
    },
  });

  // Initialize form from caseData when it loads
  useEffect(() => {
    if (!caseData) return;
    const p = caseData.patient || {};
    const dob = parseDateOnly(p.dateOfBirth) ?? null;
    const dateReceived = parseDateOnly(caseData.dateReceived) ?? null;
    const dateCollected = parseDateOnly(caseData.dateCollected) ?? null;

    const primaryIns = caseData.primaryInsurance || {};
    const secondaryIns = caseData.secondaryInsurance || {};

    form.setValues({
      patient: {
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        dateOfBirth: dob,
        sex: (p.sex as "M" | "F" | "Other" | "") || "",
        medicalRecordNumber: p.medicalRecordNumber || "",
        mobilePhone: p.mobilePhone || "",
        homePhone: p.homePhone || "",
        workPhone: p.workPhone || "",
        email: p.email || "",
        address: {
          address: p.address?.address || "",
          city: p.address?.city || "",
          state: p.address?.state || "",
          zipCode: p.address?.zipCode || "",
        },
      },
      case: {
        sendingPracticeId: caseData.sendingPracticeId || null,
        surgeryCenterLabId: caseData.surgeryCenterLabId || null,
        physicianId: caseData.physicianId || null,
        procedureId: caseData.procedureId || null,
        externalCaseNumber: caseData.externalCaseNumber || "",
        dateReceived,
        dateCollected,
        clinicalHistoryCodes: caseData.clinicalHistoryCodes || [],
        clinicalHistoryNotes: caseData.clinicalHistoryNotes || "",
        indications: caseData.indications || "",
        impressions: caseData.impressions || "",
        primaryInsurance: {
          insuranceProvider: primaryIns.insuranceProvider || "",
          planType: primaryIns.planType || "",
          policyNumber: primaryIns.policyNumber || "",
          authorizationNumber: primaryIns.authorizationNumber || "",
          groupNumber: primaryIns.groupNumber || "",
          groupName: primaryIns.groupName || "",
          patientRelationToInsured: primaryIns.patientRelationToInsured || "",
          address: {
            address: primaryIns.address?.address || "",
            city: primaryIns.address?.city || "",
            state: primaryIns.address?.state || "",
            zipCode: primaryIns.address?.zipCode || "",
          },
        },
        secondaryInsurance: {
          insuranceProvider: secondaryIns.insuranceProvider || "",
          planType: secondaryIns.planType || "",
          policyNumber: secondaryIns.policyNumber || "",
          authorizationNumber: secondaryIns.authorizationNumber || "",
          groupNumber: secondaryIns.groupNumber || "",
          groupName: secondaryIns.groupName || "",
          patientRelationToInsured: secondaryIns.patientRelationToInsured || "",
          address: {
            address: secondaryIns.address?.address || "",
            city: secondaryIns.address?.city || "",
            state: secondaryIns.address?.state || "",
            zipCode: secondaryIns.address?.zipCode || "",
          },
        },
        isRush: caseData.isRush || false,
        rushReason: caseData.rushReason || "",
        isHighPriority: caseData.isHighPriority || false,
        highPriorityReason: caseData.highPriorityReason || "",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.id]);

  // Dependent dropdowns
  const { data: surgeryCentersRaw = [] } = useSurgeryCenters();

  const patientState = form.values.patient.address.state;
  const { data: patientCities = [] } = useCities(patientState);

  const primaryInsState = form.values.case.primaryInsurance.address.state;
  const { data: primaryInsCities = [] } = useCities(primaryInsState);

  const secondaryInsState = form.values.case.secondaryInsurance.address.state;
  const { data: secondaryInsCities = [] } = useCities(secondaryInsState);

  const primaryProviderId = form.values.case.primaryInsurance.insuranceProvider;
  const {
    data: primaryPlanTypesRaw = [],
    isLoading: isPrimaryPlanTypesLoading,
  } = useInsurancePlanTypesByProvider(primaryProviderId);

  const secondaryProviderId =
    form.values.case.secondaryInsurance.insuranceProvider;
  const {
    data: secondaryPlanTypesRaw = [],
    isLoading: isSecondaryPlanTypesLoading,
  } = useInsurancePlanTypesByProvider(secondaryProviderId);

  const insuranceProviders = sanitizeOptions(
    insuranceProvidersRaw.map((p) => ({ value: p.value, label: p.label }))
  );

  const primaryPlanTypes = sanitizeOptions(
    primaryPlanTypesRaw.map((pt) => ({ value: pt.id, label: pt.type }))
  );

  const secondaryPlanTypes = sanitizeOptions(
    secondaryPlanTypesRaw.map((pt) => ({ value: pt.id, label: pt.type }))
  );

  const icdCodes = sanitizeOptions(icdCodesRaw);
  const procedures = sanitizeOptions(proceduresRaw);
  const practices = sanitizeOptions(practicesRaw);
  const surgCenters = sanitizeOptions(surgeryCentersRaw);
  const physicians = sanitizeOptions(physiciansRaw);

  // Field completion counters
  const patientFields = {
    firstName: form.values.patient.firstName,
    lastName: form.values.patient.lastName,
    dateOfBirth: form.values.patient.dateOfBirth,
    sex: form.values.patient.sex,
    medicalRecordNumber: form.values.patient.medicalRecordNumber,
    mobilePhone: form.values.patient.mobilePhone,
    email: form.values.patient.email,
    address: form.values.patient.address.address,
    state: form.values.patient.address.state,
  };
  const patientCompletion = countFields(patientFields);

  const primaryInsFields = {
    insuranceProvider: form.values.case.primaryInsurance.insuranceProvider,
    planType: form.values.case.primaryInsurance.planType,
    policyNumber: form.values.case.primaryInsurance.policyNumber,
    authorizationNumber: form.values.case.primaryInsurance.authorizationNumber,
    groupNumber: form.values.case.primaryInsurance.groupNumber,
    groupName: form.values.case.primaryInsurance.groupName,
    patientRelationToInsured:
      form.values.case.primaryInsurance.patientRelationToInsured,
  };
  const primaryInsCompletion = countFields(primaryInsFields);

  const secondaryInsFields = {
    insuranceProvider: form.values.case.secondaryInsurance.insuranceProvider,
    planType: form.values.case.secondaryInsurance.planType,
    policyNumber: form.values.case.secondaryInsurance.policyNumber,
    authorizationNumber:
      form.values.case.secondaryInsurance.authorizationNumber,
    groupNumber: form.values.case.secondaryInsurance.groupNumber,
    groupName: form.values.case.secondaryInsurance.groupName,
    patientRelationToInsured:
      form.values.case.secondaryInsurance.patientRelationToInsured,
  };
  const secondaryInsCompletion = countFields(secondaryInsFields);

  const caseInfoFields = {
    sendingPracticeId: form.values.case.sendingPracticeId,
    surgeryCenterLabId: form.values.case.surgeryCenterLabId,
    physicianId: form.values.case.physicianId,
    procedureId: form.values.case.procedureId,
    dateReceived: form.values.case.dateReceived,
    dateCollected: form.values.case.dateCollected,
    clinicalHistoryNotes: form.values.case.clinicalHistoryNotes,
  };
  const caseInfoCompletion = countFields(caseInfoFields);

  // Quick-add handlers
  const handlePracticeSubmit = async (values: PracticeFormValues) => {
    const practice = await createPractice.mutateAsync({
      name: values.name,
      shortName: values.shortName || undefined,
      primaryPhone: values.primaryPhone || undefined,
      secondaryPhone: values.secondaryPhone || undefined,
      fax: values.fax || undefined,
      preferredPathologistId: values.preferredPathologistId || undefined,
    });
    form.setFieldValue("case.sendingPracticeId", practice.id);
    closePracticeModal();
  };

  const handleSurgeryCenterSubmit = async (
    values: SurgeryCenterLabFormValues
  ) => {
    const alreadyLinked = surgCenters.some(
      (sc) =>
        sc.label.split(" (")[0].trim().toLowerCase() ===
        values.name.trim().toLowerCase()
    );
    if (alreadyLinked) {
      notifications.show({
        color: "red",
        title: "Duplicate",
        message: `"${values.name}" is already linked to this practice.`,
      });
      return;
    }
    const surgeryCenter = await createSurgeryCenter.mutateAsync({
      name: values.name,
      type: values.type,
      address: values.address || null,
      primaryPhone: values.primaryPhone || null,
      secondaryPhone: values.secondaryPhone || null,
      fax: values.fax || null,
      externalCaseNumberPrefix: values.externalCaseNumberPrefix || null,
      preferredPathologistId: values.preferredPathologistId || null,
    });
    form.setFieldValue("case.surgeryCenterLabId", surgeryCenter.id);
    closeSurgeryCenterModal();
  };

  const handlePhysicianSubmit = async (values: PhysicianFormValues) => {
    const physician = await createPhysician.mutateAsync(values);
    form.setFieldValue("case.physicianId", physician.id);
    closePhysicianModal();
  };

  const lastAutoSavedRef = useRef<string>("");
  const isAutoSaveInitialRef = useRef(true);

  const buildPayload = (v: typeof form.values) => ({
    caseId: caseData.id,
    data: {
      patient: {
        firstName: v.patient.firstName || undefined,
        lastName: v.patient.lastName || undefined,
        dateOfBirth: v.patient.dateOfBirth || undefined,
        sex: v.patient.sex || undefined,
        medicalRecordNumber: v.patient.medicalRecordNumber || undefined,
        mobilePhone: v.patient.mobilePhone || undefined,
        homePhone: v.patient.homePhone || undefined,
        workPhone: v.patient.workPhone || undefined,
        email: v.patient.email || undefined,
        address: v.patient.address,
      },
      case: {
        sendingPracticeId: v.case.sendingPracticeId || null,
        surgeryCenterLabId: v.case.surgeryCenterLabId || null,
        physicianId: v.case.physicianId || null,
        procedureId: v.case.procedureId || null,
        externalCaseNumber: v.case.externalCaseNumber || null,
        dateReceived: v.case.dateReceived || undefined,
        dateCollected: v.case.dateCollected || null,
        clinicalHistoryCodes: v.case.clinicalHistoryCodes,
        clinicalHistoryNotes: v.case.clinicalHistoryNotes || null,
        indications: v.case.indications || null,
        impressions: v.case.impressions || null,
        primaryInsurance: v.case.primaryInsurance,
        secondaryInsurance: v.case.secondaryInsurance,
        isRush: v.case.isRush,
        rushReason: v.case.rushReason || null,
        isHighPriority: v.case.isHighPriority,
        highPriorityReason: v.case.highPriorityReason || null,
      },
    },
  });

  const handleSave = () => {
    if (!caseData?.id) return;
    // Mark current values as saved so auto-save doesn't double-fire
    lastAutoSavedRef.current = JSON.stringify(form.values);
    onAutoSaveStatusChange?.("saving");
    updateMutation.mutate(buildPayload(form.values), {
      onSuccess: () => {
        onAutoSaveStatusChange?.("saved");
        setTimeout(() => onAutoSaveStatusChange?.("idle"), 3000);
        notifications.show({
          title: "Saved",
          message: "Requisition information updated",
          color: "green",
        });
        onSave?.();
      },
      onError: () => {
        lastAutoSavedRef.current = "";
        onAutoSaveStatusChange?.("error");
        setTimeout(() => onAutoSaveStatusChange?.("idle"), 5000);
        notifications.show({
          title: "Error",
          message: "Failed to save requisition information",
          color: "red",
          icon: <IconAlertCircle size={16} />,
        });
      },
    });
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isSaving: updateMutation.isPending,
  }));

  // Emit live form values for real-time widget/report updates
  const onFormValuesChangeRef = useRef(onFormValuesChange);
  onFormValuesChangeRef.current = onFormValuesChange;

  useEffect(() => {
    if (!onFormValuesChangeRef.current) return;
    const v = form.values;
    const physicianOpt = physicians.find((p) => p.value === v.case.physicianId);
    const surgCenterOpt = surgCenters.find(
      (s) => s.value === v.case.surgeryCenterLabId
    );
    onFormValuesChangeRef.current({
      patient: {
        firstName: v.patient.firstName,
        lastName: v.patient.lastName,
        dateOfBirth: v.patient.dateOfBirth,
        sex: v.patient.sex,
        medicalRecordNumber: v.patient.medicalRecordNumber,
      },
      physicianId: v.case.physicianId || null,
      physicianName: physicianOpt ? (physicianOpt as any).label : null,
      dateReceived: v.case.dateReceived,
      dateCollected: v.case.dateCollected,
      sendingPracticeId: v.case.sendingPracticeId || null,
      surgeryCenterLabId: v.case.surgeryCenterLabId || null,
      surgeryCenterName: surgCenterOpt ? (surgCenterOpt as any).label : null,
      clinicalHistoryNotes: v.case.clinicalHistoryNotes,
      indications: v.case.indications,
      impressions: v.case.impressions,
    });
  }, [form.values, physicians, surgCenters]);

  // Auto-save: debounce form values and save on change
  const [debouncedFormValues] = useDebouncedValue(form.values, 2000);

  useEffect(() => {
    // Skip first fire (on mount)
    if (isAutoSaveInitialRef.current) {
      isAutoSaveInitialRef.current = false;
      lastAutoSavedRef.current = JSON.stringify(debouncedFormValues);
      return;
    }

    if (isReadOnly || !caseData?.id) return;

    const currentString = JSON.stringify(debouncedFormValues);
    if (currentString === lastAutoSavedRef.current) return;

    lastAutoSavedRef.current = currentString;
    onAutoSaveStatusChange?.("saving");

    updateMutation.mutate(buildPayload(debouncedFormValues), {
      onSuccess: () => {
        onAutoSaveStatusChange?.("saved");
        setTimeout(() => onAutoSaveStatusChange?.("idle"), 3000);
        onSave?.();
      },
      onError: () => {
        lastAutoSavedRef.current = "";
        onAutoSaveStatusChange?.("error");
        setTimeout(() => onAutoSaveStatusChange?.("idle"), 5000);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormValues]);

  // Reset auto-save tracking when switching to a different case
  useEffect(() => {
    isAutoSaveInitialRef.current = true;
    lastAutoSavedRef.current = "";
  }, [caseData?.id]);

  return (
    <Stack gap="md">
      <Accordion
        multiple
        value={openedSections}
        onChange={setOpenedSections}
        variant="separated"
      >
        {/* ── PATIENT INFORMATION ── */}
        <Accordion.Item value="patient">
          <Accordion.Control>
            <Group justify="space-between" style={{ flex: 1 }} pr="md">
              <Title order={4}>Patient Information</Title>
              <Badge
                color={
                  patientCompletion.filled === patientCompletion.total
                    ? "green"
                    : "gray"
                }
                variant="light"
              >
                {patientCompletion.filled}/{patientCompletion.total} FIELDS
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="First Name"
                  placeholder="First name"
                  required
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.firstName")}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Last name"
                  required
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.lastName")}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <DateInput
                  label="Date of Birth"
                  placeholder="MM/DD/YYYY"
                  required
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.dateOfBirth")}
                />
                <SelectField
                  label="Sex"
                  placeholder="Select sex"
                  required
                  disabled={isReadOnly}
                  data={[
                    { value: "M", label: "Male" },
                    { value: "F", label: "Female" },
                    { value: "Other", label: "Other" },
                  ]}
                  {...form.getInputProps("patient.sex")}
                />
                <TextInput
                  label="Medical Record Number"
                  placeholder="MRN"
                  required
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.medicalRecordNumber")}
                />
              </SimpleGrid>

              <Title order={5} mt="xs">
                Contact Information
              </Title>

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput
                  label="Mobile Phone"
                  placeholder="(555) 555-5555"
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.mobilePhone")}
                />
                <TextInput
                  label="Home Phone"
                  placeholder="(555) 555-5555"
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.homePhone")}
                />
                <TextInput
                  label="Work Phone"
                  placeholder="(555) 555-5555"
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.workPhone")}
                />
              </SimpleGrid>

              <TextInput
                label="Email"
                placeholder="patient@example.com"
                type="email"
                disabled={isReadOnly}
                {...form.getInputProps("patient.email")}
              />

              <Title order={5} mt="xs">
                Address
              </Title>

              <TextInput
                label="Street Address"
                placeholder="123 Main St"
                disabled={isReadOnly}
                {...form.getInputProps("patient.address.address")}
              />

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <SelectField
                  label="State"
                  placeholder="Select state"
                  data={states}
                  searchable
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.address.state")}
                  onChange={(value: string | null) => {
                    form.setFieldValue("patient.address.state", value ?? "");
                    form.setFieldValue("patient.address.city", "");
                  }}
                />
                <SelectField
                  label="City"
                  placeholder="Select city"
                  data={patientCities}
                  searchable
                  disabled={isReadOnly || !patientState}
                  {...form.getInputProps("patient.address.city")}
                />
                <TextInput
                  label="ZIP Code"
                  placeholder="12345"
                  disabled={isReadOnly}
                  {...form.getInputProps("patient.address.zipCode")}
                />
              </SimpleGrid>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── PRIMARY INSURANCE ── */}
        <Accordion.Item value="primaryInsurance">
          <Accordion.Control>
            <Group justify="space-between" style={{ flex: 1 }} pr="md">
              <Title order={4}>Primary Insurance</Title>
              <Badge
                color={
                  primaryInsCompletion.filled > 0
                    ? primaryInsCompletion.filled === primaryInsCompletion.total
                      ? "green"
                      : "yellow"
                    : "yellow"
                }
                variant="light"
              >
                {primaryInsCompletion.filled > 0
                  ? `${primaryInsCompletion.filled}/${primaryInsCompletion.total} FIELDS`
                  : "RECOMMENDED"}
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <InsuranceSection
              fieldPrefix="case.primaryInsurance"
              form={form}
              isReadOnly={isReadOnly}
              insuranceProviders={insuranceProviders}
              planTypes={primaryPlanTypes}
              isPlanTypesLoading={isPrimaryPlanTypesLoading}
              selectedProviderId={primaryProviderId}
              states={states}
              cities={primaryInsCities}
              onStateChange={(value: string | null) => {
                form.setFieldValue(
                  "case.primaryInsurance.address.state",
                  value ?? ""
                );
                form.setFieldValue("case.primaryInsurance.address.city", "");
              }}
            />
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── SECONDARY INSURANCE ── */}
        <Accordion.Item value="secondaryInsurance">
          <Accordion.Control>
            <Group justify="space-between" style={{ flex: 1 }} pr="md">
              <Title order={4}>Secondary Insurance</Title>
              <Badge
                color={
                  secondaryInsCompletion.filled ===
                    secondaryInsCompletion.total &&
                  secondaryInsCompletion.filled > 0
                    ? "green"
                    : "gray"
                }
                variant="light"
              >
                {secondaryInsCompletion.filled}/{secondaryInsCompletion.total}{" "}
                FIELDS
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <InsuranceSection
              fieldPrefix="case.secondaryInsurance"
              form={form}
              isReadOnly={isReadOnly}
              insuranceProviders={insuranceProviders}
              planTypes={secondaryPlanTypes}
              isPlanTypesLoading={isSecondaryPlanTypesLoading}
              selectedProviderId={secondaryProviderId}
              states={states}
              cities={secondaryInsCities}
              onStateChange={(value: string | null) => {
                form.setFieldValue(
                  "case.secondaryInsurance.address.state",
                  value ?? ""
                );
                form.setFieldValue("case.secondaryInsurance.address.city", "");
              }}
            />
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── CASE INFORMATION ── */}
        <Accordion.Item value="caseInfo">
          <Accordion.Control>
            <Group justify="space-between" style={{ flex: 1 }} pr="md">
              <Title order={4}>Case Information</Title>
              <Badge
                color={
                  caseInfoCompletion.filled === caseInfoCompletion.total
                    ? "green"
                    : "gray"
                }
                variant="light"
              >
                {caseInfoCompletion.filled}/{caseInfoCompletion.total} FIELDS
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {/* Column 1 — Case Details */}
                <Stack gap="md">
                  <Title order={5}>Case Details</Title>

                  <Box>
                    <SelectField
                      label="Practice"
                      placeholder="Select practice"
                      required
                      data={practices}
                      searchable
                      disabled={isReadOnly}
                      {...form.getInputProps("case.sendingPracticeId")}
                      onChange={(value) => {
                        form.setFieldValue("case.sendingPracticeId", value);
                      }}
                    />
                    {!isReadOnly && (
                      <Group justify="flex-end" mt={4}>
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          onClick={openPracticeModal}
                        >
                          + Add New
                        </Button>
                      </Group>
                    )}
                  </Box>

                  <Box>
                    <SelectField
                      label="Surgical Center"
                      placeholder="Select surgical center"
                      required
                      data={surgCenters}
                      searchable
                      disabled={isReadOnly}
                      {...form.getInputProps("case.surgeryCenterLabId")}
                    />
                    {!isReadOnly && (
                      <Group justify="flex-end" mt={4}>
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          onClick={openSurgeryCenterModal}
                        >
                          + Add New
                        </Button>
                      </Group>
                    )}
                  </Box>

                  <Box>
                    <SelectField
                      label="Physician"
                      placeholder="Select physician"
                      required
                      data={physicians}
                      searchable
                      disabled={isReadOnly}
                      {...form.getInputProps("case.physicianId")}
                    />
                    {!isReadOnly && (
                      <Group justify="flex-end" mt={4}>
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          onClick={openPhysicianModal}
                        >
                          + Add New
                        </Button>
                      </Group>
                    )}
                  </Box>

                  <TextInput
                    label="External Case Number"
                    description="Case/specimen ID from surgery center (if applicable)"
                    placeholder="e.g., SC-2024-1234"
                    disabled={isReadOnly}
                    {...form.getInputProps("case.externalCaseNumber")}
                  />

                  <Title order={5} mt="xs">
                    Dates
                  </Title>

                  <DateInput
                    label="Date Received"
                    placeholder="MM/DD/YYYY"
                    description="When lab received the specimens"
                    required
                    disabled={isReadOnly}
                    {...form.getInputProps("case.dateReceived")}
                  />
                  <DateInput
                    label="Date Collected (Date of Service)"
                    placeholder="MM/DD/YYYY"
                    description="When the procedure was performed"
                    required
                    disabled={isReadOnly}
                    {...form.getInputProps("case.dateCollected")}
                  />
                </Stack>

                {/* Column 2 — Procedure + Clinical History */}
                <Stack gap="md">
                  <Title order={5}>Procedure</Title>
                  <SelectField
                    label="Procedure"
                    placeholder="Select procedure"
                    required
                    data={procedures}
                    searchable
                    disabled={isReadOnly}
                    {...form.getInputProps("case.procedureId")}
                  />

                  <Title order={5} mt="xs">
                    Clinical History
                  </Title>
                  <MultiSelect
                    label="Clinical History Codes (ICD-10)"
                    placeholder="Search and select ICD codes"
                    data={icdCodes}
                    searchable
                    disabled={isReadOnly}
                    {...form.getInputProps("case.clinicalHistoryCodes")}
                  />
                  <SimpleRichTextInput
                    label="Clinical History"
                    placeholder="Past medical history"
                    disabled={isReadOnly}
                    value={form.values.case?.clinicalHistoryNotes || ""}
                    onChange={(val) =>
                      form.setFieldValue("case.clinicalHistoryNotes", val)
                    }
                  />
                  <SimpleRichTextInput
                    label="Indications"
                    placeholder="Reason for procedure"
                    disabled={isReadOnly}
                    value={form.values.case?.indications || ""}
                    onChange={(val) =>
                      form.setFieldValue("case.indications", val)
                    }
                  />
                  <SimpleRichTextInput
                    label="Impressions"
                    placeholder="Procedural findings"
                    disabled={isReadOnly}
                    value={form.values.case?.impressions || ""}
                    onChange={(val) =>
                      form.setFieldValue("case.impressions", val)
                    }
                  />
                </Stack>
              </SimpleGrid>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── CASE SETTINGS (Rush + High Priority) ── */}
        <Accordion.Item value="caseSettings">
          <Accordion.Control>
            <Group justify="space-between" style={{ flex: 1 }} pr="md">
              <Title order={4}>Case Settings</Title>
              <Group gap="xs">
                {form.values.case.isRush && (
                  <Badge color="red" variant="filled" size="sm">
                    RUSH
                  </Badge>
                )}
                {form.values.case.isHighPriority && (
                  <Badge color="orange" variant="filled" size="sm">
                    HIGH PRIORITY
                  </Badge>
                )}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {/* Rush */}
              <Box
                p="sm"
                style={{
                  backgroundColor: form.values.case.isRush
                    ? "var(--mantine-color-red-0)"
                    : "transparent",
                  borderRadius: "var(--mantine-radius-sm)",
                  transition: "background-color 0.2s ease",
                }}
              >
                <Stack gap="sm">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Rush Status
                  </Text>
                  <Switch
                    label="RUSH"
                    description="Expedited processing throughout workflow"
                    checked={form.values.case.isRush}
                    onChange={(e) =>
                      form.setFieldValue("case.isRush", e.currentTarget.checked)
                    }
                    color="red"
                    size="sm"
                    disabled={isReadOnly}
                  />
                  {form.values.case.isRush && (
                    <TextInput
                      label="Rush Reason"
                      placeholder="e.g., RUSH stamp detected on requisition"
                      disabled={isReadOnly}
                      {...form.getInputProps("case.rushReason")}
                    />
                  )}
                </Stack>
              </Box>

              {/* High Priority */}
              <Box
                p="sm"
                style={{
                  backgroundColor: form.values.case.isHighPriority
                    ? "var(--mantine-color-orange-0)"
                    : "transparent",
                  borderRadius: "var(--mantine-radius-sm)",
                  transition: "background-color 0.2s ease",
                }}
              >
                <Stack gap="sm">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    High Priority
                  </Text>
                  <Switch
                    label="High Priority"
                    description="Flagged for attention (mass, tumor, cancer keywords)"
                    checked={form.values.case.isHighPriority}
                    onChange={(e) =>
                      form.setFieldValue(
                        "case.isHighPriority",
                        e.currentTarget.checked
                      )
                    }
                    color="orange"
                    size="sm"
                    disabled={isReadOnly}
                  />
                  {form.values.case.isHighPriority && (
                    <TextInput
                      label="Priority Reason"
                      placeholder="e.g., Clinical history mentions mass"
                      disabled={isReadOnly}
                      {...form.getInputProps("case.highPriorityReason")}
                    />
                  )}
                </Stack>
              </Box>
            </SimpleGrid>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {/* Quick-Add Modals */}
      <Modal.Root
        opened={practiceModalOpened}
        onClose={closePracticeModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
          header: { flexShrink: 0 },
          body: { overflowY: "auto", flex: 1 },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Practice</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <PracticeForm
              formId="practice-form-requisition"
              hideFooter
              onSubmit={handlePracticeSubmit}
            />
          </Modal.Body>
          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
              flexShrink: 0,
            }}
          >
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={closePracticeModal}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="practice-form-requisition"
                loading={createPractice.isPending}
              >
                Create
              </Button>
            </Group>
          </Box>
        </Modal.Content>
      </Modal.Root>

      <Modal
        opened={surgeryCenterModalOpened}
        onClose={closeSurgeryCenterModal}
        title="Add New Surgery Center"
        size="lg"
        closeOnClickOutside={false}
      >
        <SurgeryCenterLabForm
          onSubmit={handleSurgeryCenterSubmit}
          onCancel={closeSurgeryCenterModal}
          isSubmitting={createSurgeryCenter.isPending}
          submitLabel="Create"
          defaultType="surgery_center"
        />
      </Modal>

      <Modal.Root
        opened={physicianModalOpened}
        onClose={closePhysicianModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Physician</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <PhysicianForm onSubmit={handlePhysicianSubmit} />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closePhysicianModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="physician-form"
              loading={createPhysician.isPending}
            >
              Create
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Stack>
  );
});

export default RequisitionCenter;

// ── Internal sub-components ──

interface SelectFieldProps {
  label: string;
  placeholder: string;
  data: Array<{ value: string; label: string }>;
  searchable?: boolean;
  disabled?: boolean;
  required?: boolean;
  value?: string | null;
  onChange?: (value: string | null) => void;
  error?: string | React.ReactNode;
  description?: string;
}

function SelectField({
  label,
  placeholder,
  data,
  searchable,
  disabled,
  required,
  value,
  onChange,
  error,
  description,
}: SelectFieldProps) {
  return (
    <Select
      label={label}
      placeholder={placeholder}
      data={data}
      searchable={searchable}
      disabled={disabled}
      required={required}
      value={value ?? null}
      onChange={onChange}
      error={error}
      description={description}
    />
  );
}

interface InsuranceSectionProps {
  fieldPrefix: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  isReadOnly: boolean;
  insuranceProviders: Array<{ value: string; label: string }>;
  planTypes: Array<{ value: string; label: string }>;
  isPlanTypesLoading: boolean;
  selectedProviderId: string;
  states: Array<{ value: string; label: string }>;
  cities: Array<{ value: string; label: string }>;
  onStateChange: (value: string | null) => void;
}

function InsuranceSection({
  fieldPrefix,
  form,
  isReadOnly,
  insuranceProviders,
  planTypes,
  isPlanTypesLoading,
  selectedProviderId,
  states,
  cities,
  onStateChange,
}: InsuranceSectionProps) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select
          label="Insurance Provider"
          placeholder="Select provider"
          data={insuranceProviders}
          searchable
          disabled={isReadOnly}
          value={
            form.getInputProps(`${fieldPrefix}.insuranceProvider`).value ?? null
          }
          onChange={
            form.getInputProps(`${fieldPrefix}.insuranceProvider`).onChange
          }
          error={form.getInputProps(`${fieldPrefix}.insuranceProvider`).error}
        />
        <Select
          label="Insurance Plan Type"
          placeholder={
            selectedProviderId
              ? isPlanTypesLoading
                ? "Loading plan types..."
                : "Select plan type"
              : "Select insurance provider first"
          }
          data={planTypes}
          searchable
          disabled={isReadOnly || !selectedProviderId || isPlanTypesLoading}
          value={form.getInputProps(`${fieldPrefix}.planType`).value ?? null}
          onChange={form.getInputProps(`${fieldPrefix}.planType`).onChange}
          error={form.getInputProps(`${fieldPrefix}.planType`).error}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label="Policy Number"
          placeholder="Policy #"
          disabled={isReadOnly}
          {...form.getInputProps(`${fieldPrefix}.policyNumber`)}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label="Authorization Number"
          placeholder="Auth #"
          disabled={isReadOnly}
          {...form.getInputProps(`${fieldPrefix}.authorizationNumber`)}
        />
        <TextInput
          label="Group Number"
          placeholder="Group #"
          disabled={isReadOnly}
          {...form.getInputProps(`${fieldPrefix}.groupNumber`)}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label="Group Name"
          placeholder="Group name"
          disabled={isReadOnly}
          {...form.getInputProps(`${fieldPrefix}.groupName`)}
        />
        <Select
          label="Patient Relation to Insured"
          placeholder="Select relation"
          data={[
            { value: "Self", label: "Self" },
            { value: "Spouse", label: "Spouse" },
            { value: "Child", label: "Child" },
            { value: "Other", label: "Other" },
          ]}
          disabled={isReadOnly}
          value={
            form.getInputProps(`${fieldPrefix}.patientRelationToInsured`)
              .value ?? null
          }
          onChange={
            form.getInputProps(`${fieldPrefix}.patientRelationToInsured`)
              .onChange
          }
          error={
            form.getInputProps(`${fieldPrefix}.patientRelationToInsured`).error
          }
        />
      </SimpleGrid>

      <Title order={5} mt="xs">
        Insurance Address
      </Title>

      <TextInput
        label="Street Address"
        placeholder="123 Main St"
        disabled={isReadOnly}
        {...form.getInputProps(`${fieldPrefix}.address.address`)}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Select
          label="State"
          placeholder="Select state"
          data={sanitizeOptions(states)}
          searchable
          disabled={isReadOnly}
          value={
            form.getInputProps(`${fieldPrefix}.address.state`).value ?? null
          }
          onChange={(value: string | null) => {
            form.setFieldValue(`${fieldPrefix}.address.state`, value);
            onStateChange(value);
          }}
          error={form.getInputProps(`${fieldPrefix}.address.state`).error}
        />
        <Select
          label="City"
          placeholder="Select city"
          data={sanitizeOptions(cities)}
          searchable
          disabled={
            isReadOnly ||
            !form.getInputProps(`${fieldPrefix}.address.state`).value
          }
          value={
            form.getInputProps(`${fieldPrefix}.address.city`).value ?? null
          }
          onChange={form.getInputProps(`${fieldPrefix}.address.city`).onChange}
          error={form.getInputProps(`${fieldPrefix}.address.city`).error}
        />
        <TextInput
          label="ZIP Code"
          placeholder="12345"
          disabled={isReadOnly}
          {...form.getInputProps(`${fieldPrefix}.address.zipCode`)}
        />
      </SimpleGrid>
    </Stack>
  );
}
